import React, { useEffect, useState } from 'react';
import { Share, PlusSquare, X } from 'lucide-react';
import { safeStorageGet, safeStorageSet } from '../utils/safeStorage';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const InstallPrompt: React.FC = () => {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [showIOSPrompt, setShowIOSPrompt] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        // Check if likely on iOS
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        // Check if already in standalone mode
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone;

        if (isIOS && !isStandalone) {
            // Show iOS prompt after a delay to not be annoying
            const timer = setTimeout(() => {
                // Check if we've shown it recently
                const lastShown = safeStorageGet('pwa_ios_prompt_shown');
                const lastShownTs = lastShown ? Number(lastShown) : NaN;
                if (!Number.isFinite(lastShownTs) || Date.now() - lastShownTs > 7 * 24 * 60 * 60 * 1000) {
                    setShowIOSPrompt(true);
                    setIsVisible(true);
                }
            }, 3000);
            return () => clearTimeout(timer);
        }

        // Handle Android/Desktop install prompt
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
            // Check if we've shown it recently
            const lastShown = safeStorageGet('pwa_install_prompt_shown');
            const lastShownTs = lastShown ? Number(lastShown) : NaN;
            if (!Number.isFinite(lastShownTs) || Date.now() - lastShownTs > 7 * 24 * 60 * 60 * 1000) {
                setIsVisible(true);
            }
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const choiceResult = await deferredPrompt.userChoice;
            if (choiceResult.outcome === 'accepted') {
                console.log('User accepted the install prompt');
            }
            setDeferredPrompt(null);
            setIsVisible(false);
        }
    };

    const handleDismiss = () => {
        setIsVisible(false);
        if (showIOSPrompt) {
            safeStorageSet('pwa_ios_prompt_shown', Date.now().toString());
        } else {
            safeStorageSet('pwa_install_prompt_shown', Date.now().toString());
        }
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 z-[100] animate-fade-in-up">
            <div className="bg-brand-900/95 backdrop-blur-xl border border-brand-700/50 rounded-2xl p-4 shadow-2xl flex flex-col gap-4 max-w-md mx-auto relative overflow-hidden">
                {/* Decorative background */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/10 rounded-full blur-2xl -mr-10 -mt-10 pointer-events-none"></div>

                <button
                    onClick={handleDismiss}
                    className="absolute top-2 right-2 p-2 text-brand-400 hover:text-brand-200 transition-colors"
                >
                    <X size={20} />
                </button>

                <div className="flex items-start gap-4">
                    <img src="/logo.jpg" alt="App Icon" className="w-12 h-12 rounded-xl shadow-lg object-cover" />
                    <div className="flex-1">
                        <h3 className="text-gold-500 font-serif font-bold text-lg leading-tight mb-1">Install Aficionado</h3>
                        <p className="text-brand-300 text-sm leading-relaxed">
                            Add to your home screen for the full premium experience.
                        </p>
                    </div>
                </div>

                {showIOSPrompt ? (
                    <div className="bg-brand-950/50 rounded-xl p-3 border border-brand-800/50 flex flex-col gap-2">
                        <div className="flex items-center gap-3 text-brand-200 text-sm">
                            <span className="flex items-center justify-center w-6 h-6 bg-brand-800 rounded-full text-gold-500 font-bold text-xs">1</span>
                            <span>Tap the <Share size={16} className="inline mx-1 text-blue-400" /> Share button below</span>
                        </div>
                        <div className="flex items-center gap-3 text-brand-200 text-sm">
                            <span className="flex items-center justify-center w-6 h-6 bg-brand-800 rounded-full text-gold-500 font-bold text-xs">2</span>
                            <span>Select <span className="font-semibold text-white">Add to Home Screen</span> <PlusSquare size={16} className="inline mx-1" /></span>
                        </div>
                    </div>
                ) : (
                    <button
                        onClick={handleInstallClick}
                        className="w-full py-3 bg-gradient-to-r from-gold-600 to-gold-400 text-brand-950 font-bold rounded-xl shadow-lg shadow-gold-900/20 active:scale-95 transition-transform"
                    >
                        Install App
                    </button>
                )}
            </div>
        </div>
    );
};
